import { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search, Check, Layers } from 'lucide-react';

import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';

/**
 * Tree Select cho Teams — cây thư mục parent-child.
 * - Multi (default): chọn cha = bao gồm tất cả con. selected = string[] / onChange(ids).
 * - Single: 1 team duy nhất, KHÔNG cascade. value = string|null / onChange(id|null).
 *
 * Props:
 *  - teams: array [{id, name, parent_id, ...}]
 *  - single: bool — bật single-select mode
 *  - selected: array of team IDs (multi mode)
 *  - value: string|null (single mode)
 *  - onChange: multi → (ids) => void; single → (id|null) => void
 *  - placeholder: string
 *  - disabled: boolean
 *  - clearable: bool — chỉ áp single mode (default true)
 */
export default function TeamTreeSelect({
  teams = [], single = false, selected = [], value = null, onChange,
  placeholder = i18n._(msg`Chọn team...`), disabled = false, clearable = true,
}) {
  const {
    t
  } = useLingui();

  // Single-mode → normalize sang `selected` array để dùng chung render code phía dưới.
  if (single) {
    selected = value ? [value] : [];
  }
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      // Smart placement: nếu không đủ chỗ dưới (>= dropdown maxH 290px), mở lên trên
      const dropdownH = 290;
      const dropdownW = Math.max(r.width, 280);
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const placeAbove = spaceBelow < dropdownH && spaceAbove > spaceBelow;
      // Clamp left vào viewport (chống bị che bên phải khi parent ở SlideDrawer/ngoài rìa)
      const maxLeft = window.innerWidth - dropdownW - 8;
      const clampedLeft = Math.max(8, Math.min(r.left, maxLeft));
      setPos({
        top: placeAbove ? Math.max(8, r.top - dropdownH - 4) : r.bottom + 4,
        left: clampedLeft,
        width: dropdownW,
        maxH: placeAbove ? Math.min(dropdownH, spaceAbove - 8) : Math.min(dropdownH, spaceBelow - 8),
      });
    };
    updatePos();
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[data-team-tree-dropdown]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  // Build tree
  const { roots, childMap, teamMap, allDescendants } = useMemo(() => {
    const tm = {}; teams.forEach(t => { tm[t.id] = t; });
    const cm = {};
    // Chỉ coi là con khi CHA CÓ TRONG DANH SÁCH. Danh sách bị cắt (phân trang) hoặc lọc
    // theo quyền thì team có `parent_id` trỏ ra ngoài — trước đây nó không vào `roots`
    // (vì có parent_id) và cũng không vào `childMap` của bất kỳ gốc nào ⇒ MỒ CÔI, biến
    // mất khỏi cây: không chọn được, mà cũng không có dòng nào nói là bị thiếu.
    teams.forEach(t => {
      if (t.parent_id && tm[t.parent_id]) {
        if (!cm[t.parent_id]) cm[t.parent_id] = [];
        cm[t.parent_id].push(t);
      }
    });
    const r = teams.filter(t => !t.parent_id || !tm[t.parent_id]);
    const ad = {};
    const seen = new Set();
    const getDesc = (id) => {
      if (ad[id]) return ad[id];
      if (seen.has(id)) return [];            // chống parent_id vòng → tràn stack
      seen.add(id);
      const children = cm[id] || [];
      let all = children.map(c => c.id);
      children.forEach(c => { all = all.concat(getDesc(c.id)); });
      seen.delete(id);
      ad[id] = all;
      return all;
    };
    teams.forEach(t => getDesc(t.id));
    return { roots: r, childMap: cm, teamMap: tm, allDescendants: ad };
  }, [teams]);

  const getDescendantIds = (id) => allDescendants[id] || [];

  const isSelected = (id) => selected.includes(id);

  // Single-mode: chọn đúng 1 team (toggle off → null), không cascade.
  // Multi-mode: cascade — toggle 1 team → toggle tất cả descendants.
  const toggle = (id) => {
    if (single) {
      onChange?.(isSelected(id) && clearable ? null : id);
      return;
    }
    const descendants = getDescendantIds(id);
    const allRelated = [id, ...descendants];
    if (isSelected(id)) {
      onChange(selected.filter(s => !allRelated.includes(s)));
    } else {
      const next = new Set(selected);
      allRelated.forEach(rid => next.add(rid));
      onChange(Array.from(next));
    }
  };

  // Indeterminate: parent có 1 phần (không tất cả) descendants được chọn → semi-checked.
  // Single mode: KHÔNG có khái niệm partial (mỗi team là 1 lựa chọn độc lập).
  const isPartiallySelected = (id) => {
    if (single) return false;
    const desc = getDescendantIds(id);
    if (!desc.length) return false;
    const selectedDesc = desc.filter(d => selected.includes(d));
    return selectedDesc.length > 0 && selectedDesc.length < desc.length;
  };

  /**
   * Tập node được hiện khi đang tìm = node KHỚP + toàn bộ TỔ TIÊN của nó (để còn đường
   * đi xuống) + con cháu của node khớp (xem trọn nhánh).
   *
   * Trước đây mỗi node tự quyết định bằng "mình khớp HOẶC có CON TRỰC TIẾP khớp": nếu
   * thứ cần tìm nằm ở cháu (cấp 3+), gốc thấy mình không khớp và con trực tiếp cũng
   * không khớp ⇒ `renderNode` trả null ngay tại gốc ⇒ CẢ NHÁNH bị cắt, tìm kiếm báo
   * trống dù team đó có thật. Cây phòng ban thật sâu 3–4 cấp nên gần như luôn gặp.
   */
  const visibleIds = useMemo(() => {
    if (!search) return null;                 // null = hiện tất cả
    const q = search.toLowerCase();
    const show = new Set();
    teams.forEach(tm0 => {
      if (!tm0.name?.toLowerCase().includes(q)) return;
      show.add(tm0.id);
      (allDescendants[tm0.id] || []).forEach(d => show.add(d));
      let cur = teamMap[tm0.parent_id];
      let guard = 0;
      while (cur && guard++ < 50) { show.add(cur.id); cur = teamMap[cur.parent_id]; }
    });
    return show;
  }, [search, teams, teamMap, allDescendants]);

  // Compute display chips: nếu parent + TẤT CẢ descendants được chọn → coi như "full cascade"
  // → chỉ show 1 chip parent với icon Layers. Tránh dropdown header tràn 3-4 dòng khi chọn cha.
  // Single-mode skip logic này (chỉ 1 selection).
  const displayChips = useMemo(() => {
    if (!selected.length) return [];
    if (single) {
      return selected.map(id => ({ id, name: teamMap[id]?.name || '', cascadeAll: false }));
    }
    const covered = new Set();  // ids đã bị parent full nuốt
    const chips = [];
    // Process parents từ root → leaf (shallow first) để cha cover trước con
    const depthOf = (id) => {
      let d = 0; let cur = teamMap[id];
      while (cur?.parent_id) { d++; cur = teamMap[cur.parent_id]; if (d > 50) break; }
      return d;
    };
    const parentIds = selected
      .filter(id => (allDescendants[id] || []).length > 0)
      .sort((a, b) => depthOf(a) - depthOf(b));
    parentIds.forEach((pid) => {
      if (covered.has(pid)) return;
      const desc = allDescendants[pid] || [];
      const allSelected = desc.every(d => selected.includes(d));
      if (allSelected) {
        chips.push({ id: pid, name: teamMap[pid]?.name || '', cascadeAll: true, count: desc.length });
        covered.add(pid);
        desc.forEach(d => covered.add(d));
      }
    });
    selected.forEach((id) => {
      if (covered.has(id)) return;
      chips.push({ id, name: teamMap[id]?.name || '', cascadeAll: false });
    });
    return chips;
  }, [selected, teamMap, allDescendants, single]);

  const renderNode = (team, depth = 0) => {
    const children = childMap[team.id] || [];
    if (visibleIds && !visibleIds.has(team.id)) return null;
    const checked = isSelected(team.id);
    const partial = !checked && isPartiallySelected(team.id);
    const hasChildren = children.length > 0;

    return (
      <div key={team.id}>
        <button type="button"
          onClick={(e) => { e.stopPropagation(); toggle(team.id); }}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left transition"
          style={{ paddingLeft: `${12 + depth * 20}px` }}>
          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-blue-600 border-blue-600' : partial ? 'bg-blue-200 border-blue-400' : 'border-gray-300'}`}>
            {checked && <Check size={10} className="text-white" />}
            {partial && <span className="block w-2 h-0.5 bg-blue-700" />}
          </div>
          <span className={`text-sm truncate ${hasChildren ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{team.name}</span>
          {hasChildren && <span className="text-[10px] text-gray-400 shrink-0">({children.length})</span>}
        </button>
        {children.map(c => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div ref={ref} className="relative">
      <div onClick={() => !disabled && setOpen(!open)}
        className={`w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm cursor-pointer hover:border-blue-400 transition flex items-center min-h-[42px] ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}>
        <div className="flex-1 min-w-0">
          {displayChips.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            // 1 dòng: flex-nowrap + overflow-hidden. Chip thừa hiển thị "+N" cuối.
            (<div className="flex items-center gap-1 flex-nowrap overflow-hidden">
              {displayChips.slice(0, 3).map((c) => (
                <span key={c.id}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium shrink-0 max-w-[160px] ${
                    c.cascadeAll
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                  title={c.cascadeAll ? t`${c.name} + ${c.count} nhóm con` : c.name}>
                  {c.cascadeAll && <Layers size={11} className="shrink-0" />}
                  <span className="truncate">{c.name}</span>
                  {c.cascadeAll && <span className="text-[10px] opacity-75 shrink-0">+{c.count}</span>}
                </span>
              ))}
              {displayChips.length > 3 && (
                <span className="text-[11px] text-gray-500 shrink-0">+{displayChips.length - 3}</span>
              )}
            </div>)
          )}
        </div>
        <ChevronDown size={14} className={`text-gray-400 shrink-0 ml-2 transition ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && ReactDOM.createPortal(
        <div data-team-tree-dropdown className="fixed bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-[10010] flex flex-col"
          style={{
            top: pos.top, left: pos.left, width: pos.width || 280, maxHeight: pos.maxH || 290,
            // Ẩn cho đến khi pos được tính chuẩn (tránh chớp top-left khi mở)
            visibility: pos.width ? 'visible' : 'hidden',
          }}>
          <div className="p-2 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none" placeholder={t`Tìm team...`} autoFocus />
            </div>
            {/* Select all / Deselect all — chỉ multi mode */}
            {!single && (
              <div className="flex items-center gap-1 mt-2 px-1">
                <button type="button" onClick={() => onChange?.(teams.map(t => t.id))}
                  className="text-[11px] px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition">
                  <Trans>Chọn tất cả</Trans>
                </button>
                <button type="button" onClick={() => onChange?.([])}
                  className="text-[11px] px-2 py-1 text-gray-600 hover:bg-gray-100 rounded transition">
                  <Trans>Bỏ chọn tất cả</Trans>
                </button>
                <span className="ml-auto text-[10px] text-gray-400">{selected.length}/{teams.length} <Trans>đã chọn</Trans></span>
              </div>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {roots.map(t => renderNode(t, 0))}
            {roots.length === 0 && <p className="text-sm text-gray-400 text-center py-4"><Trans>Chưa có team nào</Trans></p>}
            {/* Tìm không ra: trước đây mọi node trả null nhưng roots.length vẫn > 0 nên
                khung nội dung TRỐNG TRƠN, không một chữ nào giải thích. */}
            {roots.length > 0 && visibleIds && visibleIds.size === 0 && (
              <p className="text-sm text-gray-400 text-center py-4"><Trans>Không tìm thấy team nào</Trans></p>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
